/**
 * The kind palette: variety decided *before* a set is generated, not hoped for after.
 *
 * The worst failure mode this product has is five near-identical substitutions. Asking a
 * model for "contrasting options" produces contrast on a good day. Sampling the
 * directions ourselves and asking for one suggestion *per direction* produces it every
 * day, and turns "the branches all felt the same" from a prompt-tuning problem into a
 * numbers problem we can unit-test.
 *
 * ## Why this lives in `@hc/shared`
 *
 * It was written for the LLM path and lived in `packages/server/src/llm/palette.ts`. The
 * heuristic engine in this directory needs exactly the same decision — which thirteenth
 * of the vocabulary does each slot get? — and two copies of a weighting table is two
 * copies that drift. So the implementation moved here and the server imports it from
 * `@hc/shared`. There is one sampler and both engines call it.
 *
 * ## The anti-trap rules
 *
 * Personalisation that narrows the graph defeats the product — see the note at the top
 * of `prefs/types.ts`. Three constraints hold it open, and each is tested:
 *
 *  1. A kind's weight may be at most **doubled** by preference signal, and at least
 *     **halved** by avoidance signal. Never zeroed. A kind the player has ignored ten
 *     times is still twice-in-a-while, not never.
 *  2. Exactly **one slot per request is sampled uniformly** across all thirteen kinds,
 *     with no weighting of any sort. This is the escape hatch: however settled a
 *     player's habits are, one branch every time is drawn from the whole vocabulary.
 *  3. A **functional anchor is always present**. Somebody doodling wants at least one
 *     option that simply works, and the anchor slot is what guarantees it survives a
 *     high adventurousness setting.
 */

import { MOVEMENT_KINDS, type MovementKind } from '../llm/constants.js';
import type { PreferenceHints } from '../prefs/types.js';

/**
 * Where each kind sits on the obvious → outlandish axis, 0..1.
 *
 * These are the numbers `adventurousness` is matched against. They are judgements, not
 * measurements, and they are in one table so that disagreeing with one is a one-line
 * change rather than an argument with the sampler.
 */
export const KIND_ADVENTUROUSNESS: Readonly<Record<MovementKind, number>> = Object.freeze({
  'functional-dominant': 0.05,
  'functional-subdominant': 0.12,
  relative: 0.2,
  parallel: 0.3,
  'secondary-dominant': 0.35,
  deceptive: 0.45,
  'stylistic-idiom': 0.5,
  modal: 0.52,
  borrowed: 0.58,
  'common-tone': 0.66,
  'chromatic-mediant': 0.8,
  planing: 0.86,
  'distant-modulation': 0.95,
});

/**
 * The kinds that count as "this simply resolves". One of these is always in the palette.
 */
export const FUNCTIONAL_ANCHOR_KINDS: readonly MovementKind[] = Object.freeze([
  'functional-dominant',
  'functional-subdominant',
  'secondary-dominant',
  'deceptive',
] as const);

/** Rule 1, upper half: preference may double a weight and no more. */
export const PERSONALISATION_MAX_MULTIPLIER = 2;

/** Rule 1, lower half: avoidance may halve a weight and no more. Never zero. */
export const PERSONALISATION_MIN_MULTIPLIER = 0.5;

/** Rule 2: how many slots ignore every signal, including adventurousness. */
export const UNIFORM_SLOTS = 1;

/** Floor on the adventurousness-derived base weight, so nothing is ever unreachable. */
export const MIN_BASE_WEIGHT = 0.08;

export interface PaletteInput {
  /** 4..6. */
  branchCount: number;
  /** 0..1. */
  adventurousness: number;
  /** Kinds the player has actually played lately — suppressed, to avoid repetition. */
  takenKinds: readonly MovementKind[];
  /** Kinds offered and passed over — suppressed more gently. */
  ignoredKinds: readonly MovementKind[];
  /** Long-run learned signal — a mild boost, capped by rule 1. */
  preferenceHints: PreferenceHints;
  /**
   * Kinds that must not be drawn at all — because a suggestion of that kind is already
   * on screen. Unlike every other signal here this one is absolute, and it is the one
   * case where rule 3 can be waived: if every anchor is already offered, a second one
   * would be dropped by the set accumulator anyway, so the anchor slot becomes an
   * ordinary weighted pick rather than a duplicate.
   */
  excludeKinds?: readonly MovementKind[];
  /** Injected for deterministic tests. */
  random?: () => number;
}

/**
 * How well a kind matches the requested adventurousness.
 *
 * A triangular kernel centred on `adventurousness`: at 0 the functional kinds dominate,
 * at 1 the mediants and planing do, and in between it slides continuously. The floor
 * keeps every kind reachable at every setting, which is rule 1 restated for the base
 * weight.
 */
export function baseWeight(kind: MovementKind, adventurousness: number): number {
  const distance = Math.abs(KIND_ADVENTUROUSNESS[kind] - clamp01(adventurousness));
  return Math.max(MIN_BASE_WEIGHT, 1 - distance);
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0.5;
  return Math.min(1, Math.max(0, value));
}

function countOf(kinds: readonly MovementKind[], kind: MovementKind): number {
  let count = 0;
  for (const entry of kinds) if (entry === kind) count += 1;
  return count;
}

/**
 * The personalisation factor for one kind, clamped to
 * `[PERSONALISATION_MIN_MULTIPLIER, PERSONALISATION_MAX_MULTIPLIER]`.
 *
 * Exported because the clamp is the promise this module makes to the player, and a
 * promise that is not directly testable is a promise that quietly stops being true.
 */
export function personalisationMultiplier(kind: MovementKind, input: PaletteInput): number {
  let multiplier = 1;

  // Recently played: bias away, so consecutive branch sets do not repeat themselves.
  multiplier /= 1 + 0.5 * countOf(input.takenKinds, kind);

  // Offered and passed over: a gentler nudge. The player may simply not have got to it.
  multiplier /= 1 + 0.35 * countOf(input.ignoredKinds, kind);

  // Long-run taste: a mild pull toward what this player actually reaches for.
  const counts = input.preferenceHints.takenKindCounts;
  let total = 0;
  for (const entry of MOVEMENT_KINDS) total += Math.max(0, counts[entry] ?? 0);
  if (total > 0) {
    const share = Math.max(0, counts[kind] ?? 0) / total;
    multiplier *= 1 + share;
  }

  return Math.min(
    PERSONALISATION_MAX_MULTIPLIER,
    Math.max(PERSONALISATION_MIN_MULTIPLIER, multiplier),
  );
}

/** Final sampling weight per kind: adventurousness fit times the clamped personalisation. */
export function kindWeights(input: PaletteInput): Record<MovementKind, number> {
  const weights = {} as Record<MovementKind, number>;
  for (const kind of MOVEMENT_KINDS) {
    weights[kind] =
      baseWeight(kind, input.adventurousness) * personalisationMultiplier(kind, input);
  }
  return weights;
}

function weightedPick(
  pool: readonly MovementKind[],
  weights: Record<MovementKind, number>,
  random: () => number,
): MovementKind {
  let total = 0;
  for (const kind of pool) total += weights[kind];
  // Every weight is strictly positive by construction, so this is belt-and-braces.
  if (total <= 0) return pool[Math.floor(random() * pool.length) % pool.length] as MovementKind;

  let threshold = random() * total;
  for (const kind of pool) {
    threshold -= weights[kind];
    if (threshold <= 0) return kind;
  }
  return pool[pool.length - 1] as MovementKind;
}

function uniformPick(pool: readonly MovementKind[], random: () => number): MovementKind {
  const index = Math.min(pool.length - 1, Math.floor(random() * pool.length));
  return pool[index] as MovementKind;
}

function shuffle<T>(items: T[], random: () => number): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    const a = out[i] as T;
    const b = out[j] as T;
    out[i] = b;
    out[j] = a;
  }
  return out;
}

/**
 * Draw `branchCount` distinct movement kinds.
 *
 * Slot order is meaningful: the functional anchor is returned **first** so that, when
 * the response is streamed, the first branch to appear on screen is the one that simply
 * works. The remaining slots — including the uniform one — are shuffled, so the escape
 * hatch is not always in the same position on screen.
 */
export function samplePalette(input: PaletteInput): MovementKind[] {
  const random = input.random ?? Math.random;
  const excluded = new Set(input.excludeKinds ?? []);
  const available = MOVEMENT_KINDS.filter((kind) => !excluded.has(kind));
  const count = Math.max(0, Math.min(available.length, Math.round(input.branchCount)));
  if (count === 0) return [];
  const weights = kindWeights(input);

  const remaining = new Set<MovementKind>(available);

  // Rule 3 — the anchor, chosen among the functional kinds by weight. When every anchor
  // is already on screen there is no anchor to add, so the slot is filled like any other.
  const anchorPool = FUNCTIONAL_ANCHOR_KINDS.filter((kind) => remaining.has(kind));
  const anchor =
    anchorPool.length > 0
      ? weightedPick(anchorPool, weights, random)
      : weightedPick([...remaining], weights, random);
  remaining.delete(anchor);
  const rest: MovementKind[] = [];

  // Rule 2 — the unweighted slot. No adventurousness, no preference, no avoidance.
  if (rest.length + 1 < count) {
    const uniform = uniformPick([...remaining], random);
    remaining.delete(uniform);
    rest.push(uniform);
  }

  while (rest.length + 1 < count && remaining.size > 0) {
    const picked = weightedPick([...remaining], weights, random);
    remaining.delete(picked);
    rest.push(picked);
  }

  return [anchor, ...shuffle(rest, random)];
}

/**
 * One line per kind, for the LLM branch prompt.
 *
 * Prompt text, and the only thing in this file that is. It travels with the vocabulary
 * rather than with the prompt because it is a table keyed by `MovementKind` and every
 * other such table is here; splitting it off would leave two files to keep in step when
 * the vocabulary changes.
 */
export const KIND_BRIEFS: Readonly<Record<MovementKind, string>> = Object.freeze({
  'functional-dominant': 'a dominant that resolves — the move that simply works',
  'functional-subdominant': 'a subdominant motion — opening out rather than resolving',
  relative: 'the relative major or minor, or another shared-key-signature neighbour',
  parallel: 'the same root, opposite mode',
  modal: 'a chord drawn from a mode other than the prevailing one',
  'chromatic-mediant': 'a third away with a chromatic root motion, sharing one or two tones',
  borrowed: 'a chord borrowed from the parallel key',
  'common-tone': 'a move held together by one sustained note rather than by function',
  'secondary-dominant': 'a dominant of some chord other than the tonic',
  deceptive: 'a resolution that lands somewhere other than the expected tonic',
  planing: 'the same shape moved in parallel, function suspended',
  'distant-modulation': 'a pivot to a genuinely remote key',
  'stylistic-idiom': 'a move that belongs to a specific idiom or repertoire',
});
